import React, { ChangeEvent, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IBaseComponent } from '../../sharedInterfaces/IBaseComponent';
import { setCardPurpose, setCardPurposeAdditionalInfo } from '~/actions/formActions';
import { setMissingError } from '~/actions/errorActions';
import { CARD_PURPOSE, CARD_PURPOSE_ADDITIONAL_INFO } from '~/actions/actionTypes';
import DropDown from '../Base/DropDown/DropDown';
import Input from '../Base/Input/Input';
import { IState } from '~/sharedInterfaces/IState';
import { IBaseInput, IDropDownOption } from '~/sharedInterfaces/SharedInterfaces';
import { getCardPurposeLabels } from '~/services/DefaultLabelNameService';
import { pushToGTM } from '~/actions/gtmActions';

interface IApplicationReason {
  reason: string;
  reasonText: string | null;
}

export interface ICardPurpose extends IBaseInput {
  purpose?: string;
  additionalInfo?: string;
  additionalInfoLabel?: string;
  cardPurposeValues?: IDropDownOption[];
  applicationReasons?: IApplicationReason[];
}

interface ICardPurposeProps extends ICardPurpose, IBaseComponent {}

const CardPurpose: FC<ICardPurposeProps> = (props) => {
  const dispatch = useDispatch();

  const value = useSelector((state: IState) => state.signup.cardPurpose);
  const cardPurposeError = useSelector((state: IState) => state.error.cardPurpose?.missing);
  const cardPurposeAdditionalInfoError = useSelector(
    (state: IState) => state.error.cardPurposeAdditionalInfo?.missing
  );
  const { locale } = useSelector((state: IState) => state.widgetContext);
  const cardPurpose = value?.applicationReasons?.[0].reason || '';
  const additionalInfo = value?.applicationReasons?.[0].reasonText || '';

  const { label, additionalInfoLabel, cardPurposeValues } = getCardPurposeLabels(locale);
  const componentLabel = props.label || label;

  const onChangePurpose = (event: ChangeEvent<any>) => {
    const inputValue = event.target.value;
    dispatch(setCardPurpose(inputValue));
    if (inputValue === 'OtherPurpose') {
      dispatch(setCardPurposeAdditionalInfo(null));
    }
    dispatch(
      pushToGTM({
        event: 'q_user_a_card_purpose_select',
      })
    );
    if (cardPurposeError) dispatch(setMissingError(CARD_PURPOSE, ''));
  };

  const onChangeAdditional = (event: ChangeEvent<any>) => {
    const inputValue = event.target.value;
    dispatch(setCardPurposeAdditionalInfo(inputValue));
    if (cardPurposeAdditionalInfoError) {
      dispatch(setMissingError(CARD_PURPOSE_ADDITIONAL_INFO, ''));
    }
  };

  return (
    <>
      <DropDown
        {...props}
        label={componentLabel}
        value={cardPurpose}
        dropDownValues={props.cardPurposeValues || cardPurposeValues}
        onChange={onChangePurpose}
        analyticsEvent="card-purpose"
        error={cardPurposeError}
      />
      {cardPurpose === 'OtherPurpose' && (
        <Input
          {...props}
          tooltip={false}
          label={props.additionalInfoLabel || additionalInfoLabel}
          error={cardPurposeAdditionalInfoError}
          onChange={onChangeAdditional}
          value={additionalInfo}
          analyticsEvent="card-purpose"
          maxLength={200}
        />
      )}
    </>
  );
};

export default CardPurpose;
